import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

import {
  filterTeamsByPickerRow,
  flattenOrgPickerRows,
  resolvePickerRowFromUrl,
  subOrgIdForPickerRow,
} from 'js/components/OrgNodePicker/flattenOrgPickerRows';
import {
  buildOrgTrendsDetailPath,
  teamIdFromOrgDetailSearch,
} from 'js/utils/org-detail-url';

const OrgTrendsContext = createContext(null);

export function OrgTrendsProvider({
  organization,
  workspace,
  assessmentKey,
  match,
  history,
  location,
  questionSetKeys = [],
  categoryLookups = new Map(),
  buildPickerPath = null,
  debounceTrendPoints = true,
  children,
}) {
  const pickerRows = useMemo(
    () => flattenOrgPickerRows(organization),
    [organization]
  );

  const subOrgId = +(match?.params?.subOrgId ?? organization?.id ?? 0);
  const teamId = teamIdFromOrgDetailSearch(location?.search);

  const selectedPickerRow = useMemo(
    () => resolvePickerRowFromUrl(pickerRows, subOrgId, teamId),
    [pickerRows, subOrgId, teamId]
  );

  const [expandedKeys, setExpandedKeys] = useState(() => new Set());
  const [hoveredLine, setHoveredLine] = useState(null);
  const [lockedLine, setLockedLine] = useState(null);

  const setSelectedPickerRow = useCallback(
    (row) => {
      if (!row) {
        return;
      }

      if (!workspace?.id || organization?.id == null) {
        return;
      }

      const pathBuilder = buildPickerPath ?? buildOrgTrendsDetailPath;
      const nextLocation = pathBuilder({
        workspaceId: workspace.id,
        orgId: organization.id,
        assessmentKey,
        subOrgId: subOrgIdForPickerRow(row),
        teamId: row.type === 'team' ? row.id : null,
      });

      if (
        history.location.pathname !== nextLocation.pathname ||
        history.location.search !== nextLocation.search
      ) {
        history.replace({
          ...nextLocation,
          state: { noScroll: true },
        });
      }
    },
    [workspace?.id, organization?.id, assessmentKey, history, buildPickerPath]
  );

  const filterTeams = useCallback(
    (teams) => filterTeamsByPickerRow(teams, selectedPickerRow),
    [selectedPickerRow]
  );

  const toggleExpanded = useCallback((questionSetKey) => {
    setExpandedKeys((current) => {
      const next = new Set(current);

      if (next.has(questionSetKey)) {
        next.delete(questionSetKey);
      } else {
        next.add(questionSetKey);
      }

      return next;
    });
  }, []);

  const expandAll = useCallback(() => {
    setExpandedKeys(new Set(questionSetKeys));
  }, [questionSetKeys]);

  const collapseAll = useCallback(() => {
    setExpandedKeys(new Set());
  }, []);

  const allExpanded =
    questionSetKeys.length > 0 &&
    questionSetKeys.every((key) => expandedKeys.has(key));

  const setHoveredLineFromMatch = useCallback((line) => {
    setHoveredLine((current) => {
      const nextId = line?.id ?? null;
      const currentId = current?.id ?? null;

      if (nextId === currentId) {
        return current;
      }

      return line;
    });
  }, []);

  const clearHoveredLine = useCallback(() => {
    setHoveredLine(null);
  }, []);

  const toggleLockedLine = useCallback((line) => {
    if (!line?.id) {
      return;
    }

    setLockedLine((current) =>
      current?.id === line.id ? null : { ...line, id: String(line.id) }
    );
  }, []);

  const clearLockedLine = useCallback(() => {
    setLockedLine(null);
  }, []);

  const activeLine = hoveredLine ?? lockedLine;
  const activeLineId = activeLine?.id ?? null;

  const value = useMemo(
    () => ({
      organization,
      pickerRows,
      selectedPickerRow,
      setSelectedPickerRow,
      filterTeams,
      expandedKeys,
      toggleExpanded,
      expandAll,
      collapseAll,
      allExpanded,
      hasQuestionSets: questionSetKeys.length > 0,
      categoryLookups,
      showCategoryPills: categoryLookups.size > 1,
      hoveredLine,
      lockedLine,
      activeLine,
      activeLineId,
      activeLineLabel: activeLine?.label ?? null,
      setHoveredLineFromMatch,
      clearHoveredLine,
      toggleLockedLine,
      clearLockedLine,
      debounceTrendPoints,
    }),
    [
      organization,
      pickerRows,
      selectedPickerRow,
      setSelectedPickerRow,
      filterTeams,
      expandedKeys,
      toggleExpanded,
      expandAll,
      collapseAll,
      allExpanded,
      questionSetKeys.length,
      categoryLookups,
      hoveredLine,
      lockedLine,
      activeLine,
      activeLineId,
      setHoveredLineFromMatch,
      clearHoveredLine,
      toggleLockedLine,
      clearLockedLine,
      debounceTrendPoints,
    ]
  );

  return (
    <OrgTrendsContext.Provider value={value}>
      {children}
    </OrgTrendsContext.Provider>
  );
}

export function useOrgTrends() {
  const context = useContext(OrgTrendsContext);

  if (!context) {
    throw new Error('useOrgTrends must be used within OrgTrendsProvider');
  }

  return context;
}
